// src/ui/auth/GooglePhoneNumberPage.jsx
//
// Same UI/flow as PhoneAuthPage, minus the OTP step: after Google sign-in,
// a new user still needs a phone number on file for the rest of the app to
// work the way it already does. We just carry it forward in navigation
// state to AddProfilePage, which does the actual Firestore write together
// with the rest of the profile — same as verificationId does in the OTP
// flow, nothing is persisted here on its own.

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCountryByDialCode } from '../../utils/countries';

export const GooglePhoneNumberPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const state = location.state || {};
  const uid = state.uid || '';
  const email = state.email || null;
  const displayName = state.displayName || null;

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [code, setCode] = useState('+');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showDivider, setShowDivider] = useState(false);
  const [isAutoFilling, setIsAutoFilling] = useState(false);

  const codeInputRef = useRef(null);
  const numberInputRef = useRef(null);

  // ── Guard: bounce back if we landed here without a uid ─────────────────────

  useEffect(() => {
    if (!uid) {
      navigate('/auth', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  // ── Computed ──────────────────────────────────────────────────────────────

  const getFullPhone = () => {
    if (!selectedCountry) return '';
    const raw = phoneNumber.trim();
    const normalized = raw.startsWith('0') ? raw.substring(1) : raw;
    return `+${selectedCountry.dialCode}${normalized}`;
  };

  const isValid = selectedCountry && phoneNumber.trim().length >= 8;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleBack = () => {
    navigate(-1);
  };

  const handlePickCountry = () => {
    navigate('/auth/country', {
      state: { returnTo: '/auth/google-phone' }
    });
  };

  const applySelectedCountry = (country) => {
    setSelectedCountry(country);
    setCode(`+${country.dialCode}`);
    setShowDivider(true);
    setTimeout(() => {
      numberInputRef.current?.focus();
    }, 0);
  };

  useEffect(() => {
    const returnedCountry = location.state?.selectedCountry;
    if (returnedCountry) {
      applySelectedCountry(returnedCountry);
      // Preserve uid/email/displayName while clearing the returned country
      // so refreshing / re-navigating doesn't re-apply it
      navigate(location.pathname, {
        replace: true,
        state: { uid, email, displayName },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const detectCountryFromCode = (value) => {
    if (isAutoFilling) return;

    if (!value.startsWith('+')) {
      setCode(`+${value}`);
      return;
    }

    const codeWithoutPlus = value.substring(1);
    const country = getCountryByDialCode(codeWithoutPlus);

    if (country) {
      setSelectedCountry(country);
      setShowDivider(true);

      if (!isAutoFilling && codeWithoutPlus.length > 0) {
        setIsAutoFilling(true);
        setTimeout(() => {
          numberInputRef.current?.focus();
          setIsAutoFilling(false);
        }, 100);
      }
    }
  };

  const handleCodeFieldClick = () => {
    if (!code || code === '') {
      setCode('+');
    } else if (!code.startsWith('+')) {
      setCode(`+${code}`);
    }
  };

  const handleContinue = () => {
    if (!isValid) return;

    const fullPhone = getFullPhone();

    navigate('/auth/add-profile', {
      state: {
        uid,
        phoneNumber: fullPhone,
        email,
        displayName,
      },
    });
  };

  // ── Init ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (location.state?.selectedCountry) return;

    setCode('+');
    setTimeout(() => {
      codeInputRef.current?.focus();
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-filatom-bg relative">
      <div className="max-w-md mx-auto px-6 h-screen flex flex-col">
        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto pb-24">
          <div className="pt-10">
            <h1 className="text-white text-xl font-medium text-center">Phone number</h1>
            <p className="text-white/50 text-[15px] leading-tight text-center mt-2">
              Please confirm your country code<br />and enter your phone number.
            </p>
          </div>

          {/* Phone Input Card */}
          <div className="mt-8">
            <div className="bg-filatom-card rounded-[20px] overflow-hidden">
              {/* Country Selector */}
              <button
                onClick={handlePickCountry}
                className="w-full flex items-center justify-between px-4 py-2 text-white text-[15px] hover:bg-white/[0.03] transition-colors"
              >
                <span>
                  {selectedCountry
                    ? `${selectedCountry.flag} ${selectedCountry.name}`
                    : 'Country'}
                </span>
                <svg className="w-[22px] h-[22px] text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <div className="h-px bg-white/10 mx-4" />

              {/* Code + Number */}
              <div className="flex items-center px-4 py-2">
                <div className="w-[70px] flex-shrink-0">
                  <input
                    ref={codeInputRef}
                    type="text"
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value);
                      detectCountryFromCode(e.target.value);
                    }}
                    onClick={handleCodeFieldClick}
                    className="w-full bg-transparent text-white text-[15px] outline-none py-2"
                    placeholder="+"
                  />
                </div>

                {showDivider && (
                  <div className="w-px h-[22px] bg-filatom-primary mx-2 flex-shrink-0" />
                )}

                <div className="flex-1">
                  <input
                    ref={numberInputRef}
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      setPhoneNumber(val);
                    }}
                    placeholder={showDivider ? 'Phone number' : ''}
                    disabled={!showDivider}
                    className="w-full bg-transparent text-white text-[15px] outline-none placeholder:text-white/30 py-2 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fixed bottom button */}
        <div className="absolute left-6 right-6 bottom-5 max-w-md mx-auto w-[calc(100%-3rem)]">
          <button
            onClick={handleContinue}
            disabled={!isValid}
            className={`w-full py-3 rounded-2xl text-sm font-medium transition-all ${
              isValid
                ? 'text-white hover:opacity-90 active:scale-98'
                : 'bg-filatom-card text-gray-500 cursor-not-allowed'
            }`}
            style={isValid ? { backgroundColor: '#FF8C08' } : undefined}
          >
            Next
          </button>
        </div>

        {/* Back button */}
        <button
          onClick={handleBack}
          className="absolute top-4 left-0 p-2 text-white/70 hover:text-white transition-colors"
          aria-label="Back"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default GooglePhoneNumberPage;